import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { addOnSchema, type AddOnFormValues } from '../schemas/addon.schema';
import { Form, Input, Button, Label } from '@/shared/components';
import { useFeeReferenceDataQuery } from '../hooks/useFeeReferenceDataQuery';
import { useVatDerivedCost } from '../hooks/useVatDerivedCost';
import { VatCostFields } from './VatCostFields/VatCostFields';
import type { AddOnRecord } from '../services/smr-admin';
import { twoPlaceMoney } from '../utils/twoPlaceMoney';

type AddOnFormInput = z.input<typeof addOnSchema>;

interface AddOnFormProps {
    initialValues?: Partial<AddOnRecord>;
    onSubmit: (data: AddOnFormValues) => void;
    isLoading?: boolean;
    submitLabel?: string;
}

export function AddOnForm({ initialValues, onSubmit, isLoading = false, submitLabel = 'Save' }: AddOnFormProps) {
    const { data: refData, isLoading: isRefLoading } = useFeeReferenceDataQuery();
    const vatCategories = refData?.vat_categories ?? [];

    const { handleSubmit, control, watch, setValue, formState: { isSubmitting } } = useForm<AddOnFormInput, unknown, AddOnFormValues>({
        resolver: zodResolver(addOnSchema),
        defaultValues: {
            name:             initialValues?.name ?? '',
            description:      initialValues?.description ?? null,
            category:         initialValues?.category ?? null,
            vat_category_id:  initialValues?.vat_category_id ?? '',
            price:            twoPlaceMoney(initialValues?.price) ?? undefined,
            gross_price:      twoPlaceMoney(initialValues?.gross_price) ?? undefined,
        },
    });

    useVatDerivedCost({
        watch,
        setValue,
        netField:      'price',
        grossField:    'gross_price',
        categoryField: 'vat_category_id',
        categories:    vatCategories,
    });

    return (
        <Form className="space-y-4 max-w-2xl" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                    <Label htmlFor="name">Name</Label>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input id="name" placeholder="Optional extra name" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                        )}
                    />
                </div>
                <div className="md:col-span-2">
                    <Label htmlFor="description">Description <span className="text-mp-muted">(optional)</span></Label>
                    <Controller
                        name="description"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input id="description" placeholder="Description" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                        )}
                    />
                </div>
                <div>
                    <Label htmlFor="category">Category <span className="text-mp-muted">(optional)</span></Label>
                    <Controller
                        name="category"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input id="category" placeholder="e.g. cover, cosmetic" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                        )}
                    />
                </div>
                <VatCostFields<AddOnFormInput>
                    control={control}
                    categoryField="vat_category_id"
                    netField="price"
                    grossField="gross_price"
                    categories={vatCategories}
                    isLoadingCategories={isRefLoading}
                />
            </div>
            <Button size="sm" disabled={isLoading || isSubmitting}>
                {isLoading || isSubmitting ? 'Saving...' : submitLabel}
            </Button>
        </Form>
    );
}
